import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { PrimaryButton } from "@/components/fionova/Primitives";
import { AccountLink } from "@/components/journey/AccountLink";
import { checkInvite, redeemInvite } from "@/services/unsaidService";
import { useAuth } from "@/context/AuthContext";
import { journey } from "@/lib/journey";

// Invited reviewers/partners: verify the code, link ONE account (new or existing), then the server uses the code
// and starts 30 days of access.
export const InviteRedeem = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [checked, setChecked] = useState(null); // { email_locked }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState(false);

  const redeem = async () => {
    setError("");
    const res = await redeemInvite(code.trim());
    if (!res.ok) {
      setActive(res.status === 409);
      setError(res.data?.detail || "That code couldn't be verified.");
      return;
    }
    journey.off();
    await refresh();
    navigate("/app?chat=1&welcome=reviewer");
  };

  const verify = async () => {
    if (!code.trim() || busy) return;
    setBusy(true); setError("");
    const res = await checkInvite(code.trim());
    if (!res.ok) { setBusy(false); setError(res.data?.detail || "That code couldn't be verified."); return; }
    if (user) await redeem(); else setChecked(res.data);
    setBusy(false);
  };

  if (checked && !user) {
    return (
      <div className="space-y-4" data-testid="invite-account-step">
        <p className="text-[14.5px] leading-relaxed text-slate-700" data-testid="invite-verified">
          Your invitation code is valid. Now choose the email you'd like to use with FioNova. If you already have an account, just sign in. Your invitation attaches to that same account, with no second account or password.
        </p>
        <AccountLink onDone={redeem} note={checked.email_locked ? "This invitation is tied to a specific email. Please use the email address your invitation was sent to." : null} />
        {error && <p className="text-sm text-rose-600" data-testid="invite-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="invite-redeem">
      <p className="text-[14.5px] leading-relaxed text-slate-600">
        If you were personally invited to review FioNova, enter the invitation code you received. Codes are single-use and verified securely.
      </p>
      <div className="flex items-center gap-2 rounded-2xl bg-white/85 px-4 ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-teal-500">
        <KeyRound className="h-4 w-4 text-slate-400" />
        <input data-testid="invite-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") verify(); }} placeholder="FIO-XXXX-XXXX-XXXX" autoComplete="off"
          className="w-full bg-transparent py-3 font-mono text-[15px] tracking-wider text-slate-900 placeholder:text-slate-400 focus:outline-none" />
      </div>
      {error && <p className="text-sm text-rose-600" data-testid="invite-error">{error}</p>}
      {active && <PrimaryButton data-testid="invite-go-app" onClick={() => navigate("/app")}>Go to my FioNova</PrimaryButton>}
      {!active && (
        <PrimaryButton data-testid="invite-submit" onClick={verify} disabled={!code.trim() || busy}>
          {busy ? "Verifying…" : user ? "Activate my invitation" : "Verify my invitation"}
        </PrimaryButton>
      )}
      <p className="text-center text-xs text-slate-500">Includes 30 days of complimentary full access, starting when your code is activated.</p>
    </div>
  );
};
